import React, { FC, useEffect, useState, useRef } from 'react';
import { CardContainer } from '../../layout';
import { applyQueueDistribution, getQueueDistributions } from '../../services';
import { Badge, Button, Spinner, Table, ToggleSwitch } from 'flowbite-react';
import { TimeAgo } from '../../components';
import { HiClock } from 'react-icons/hi';

const listBadge = (status: string) => {
  switch (status) {
    case 'pending':
      return (
        <Badge color="warning" icon={HiClock}>
          Pending
        </Badge>
      );
    case 'retry':
      return (
        <Badge color="warning" icon={HiClock}>
          Retry
        </Badge>
      );
    default:
      return <Badge color="success">Success</Badge>;
  }
};

const listSite = (siteId: number) => {
  const sites: Record<number, string> = {
    1: 'Betkanyon',
    2: 'Winxbet',
    3: 'Bahisal',
    4: 'WinxbetBr',
    5: 'Unibahis',
    7: 'Betloto',
    8: 'Kasino',
  };

  return sites[siteId];
};

export const QueueDistribution: FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [queueDistributions, setQueueDistributions] = useState<any[]>([]);
  const [queueCount, setQueueCount] = useState<number>(0);
  const [loadingButtons, setLoadingButtons] = useState<Record<string, boolean>>(
    {},
  );
  const [autoSend, setAutoSend] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const timeoutRef = useRef<any>(null);
  const countdownRef = useRef<any>(null);

  const fetchQueueDistributions = async () => {
    try {
      const response: any = await getQueueDistributions();
      setQueueDistributions(response?.data?.pendingInvitations || []);
      setQueueCount(response?.data?.count || 0);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (id: string) => {
    try {
      setLoadingButtons((prev) => ({ ...prev, [id]: true }));

      await applyQueueDistribution({
        id,
      });
    } catch (error) {
      console.error('Gönderme işlemi başarısız:', error);
    } finally {
      // remove loading button
      setLoadingButtons((prev) => ({ ...prev, [id]: false }));

      // remove queue distribution
      setQueueDistributions((prev) => prev.filter((item) => item._id !== id));

      // update queue count
      setQueueCount(queueCount - 1);
    }
  };

  useEffect(() => {
    fetchQueueDistributions();
  }, []);

  useEffect(() => {
    const scheduleNextSend = () => {
      // Otomatik gönderim kapalıysa veya kuyruk boşsa durdur
      if (!autoSend || queueDistributions.length === 0) {
        setCountdown(null);

        // Zamanlayıcıları temizle
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        return;
      }

      // Rastgele süre belirle (10-20 saniye arası)
      const MIN_INTERVAL = 10;
      const MAX_INTERVAL = 20;
      const randomInterval =
        Math.floor(Math.random() * (MAX_INTERVAL - MIN_INTERVAL)) +
        MIN_INTERVAL;
      const interval = randomInterval * 1000;

      // Zamanlayıcıları temizle
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);

      // Geri sayım başlangıç değeri
      setCountdown(interval);

      // Her saniye geri sayımı güncelle
      countdownRef.current = setInterval(() => {
        setCountdown((current) => {
          // Eğer null ise veya 1000ms'den az kaldıysa interval'ı durdur
          if (current === null || current <= 1000) {
            clearInterval(countdownRef.current);
            return 0;
          }
          // Her saniye 1000ms azalt
          return current - 1000;
        });
      }, 1000);

      // Ana zamanlayıcı - süre dolunca işlemi gerçekleştir
      timeoutRef.current = setTimeout(() => {
        // Eğer hala kuyrukta eleman varsa işlemi gerçekleştir
        if (queueDistributions.length > 0) {
          const { _id: id } = queueDistributions[0];

          handleSend(id);
          console.log('autoSend triggered with interval:', interval, 'ms', id);

          // Kuyruğu güncelle
          setQueueDistributions((prev) =>
            prev.filter((item) => item._id !== id),
          );
          setQueueCount((prev) => prev - 1);

          // Recursion: Sonraki gönderimi planla
          scheduleNextSend();
        } else {
          setCountdown(null);
        }
      }, interval);
    };

    // Otomatik gönderim durumu değiştiğinde
    if (autoSend) {
      scheduleNextSend();
    } else {
      // Zamanlayıcıları temizle
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setCountdown(null);
    }

    // Cleanup
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoSend, queueDistributions]);

  return (
    <CardContainer title={`Mesaj Kuyruğu (${queueCount})`}>
      {loading ? (
        <div className="text-center">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="flex justify-end mb-4 items-center">
            <div className="text-sm text-gray-500 mr-2 ">
              {autoSend
                ? `Otomatik gönderim aktif ${
                    countdown
                      ? `(${Math.max(1, Math.ceil(countdown / 1000))} sn)`
                      : ''
                  }`
                : 'Otomatik gönderim devre dışı'}
            </div>
            <ToggleSwitch
              checked={autoSend}
              label="Otomatik Gönder"
              onChange={() => setAutoSend(!autoSend)}
            />
          </div>
          <Table striped hoverable className="table-fixed w-full">
            <Table.Body>
              {queueDistributions.map((queueDistribution: any) => {
                const { status, isLowPriority } = queueDistribution;
                const isButtonLoading =
                  loadingButtons[queueDistribution._id] || false;

                return (
                  <Table.Row
                    key={queueDistribution._id}
                    className={
                      isLowPriority
                        ? '!bg-gray-100 !opacity-60 dark:!bg-gray-900'
                        : ''
                    }
                  >
                    <Table.Cell className="w-1/2">
                      <div className="flex flex-col gap-2">
                        <div className="text-xs text-gray-500">
                          {queueDistribution.message}
                        </div>
                        <div className="flex gap-1">
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                            1A: {queueDistribution.messageCount1Month || 0}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">
                            3A: {queueDistribution.messageCount3Months || 0}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">
                            Tüm: {queueDistribution.messageCountTotal || 0}
                          </span>
                          <span className="px-2 py-0.5 text-xs bg-gray-100 rounded">
                            ID: {queueDistribution._id}
                          </span>
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="w-1/6">
                      <span className="text-xs text-gray-500">
                        {listSite(queueDistribution.siteId)}
                      </span>
                    </Table.Cell>
                    <Table.Cell className="w-1/6">
                      <div className="flex justify-center">
                        {listBadge(status)}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="w-1/6">
                      <span className="text-xs text-gray-500">
                        <TimeAgo date={queueDistribution.createdAt} />
                      </span>
                    </Table.Cell>
                    <Table.Cell className="w-1/6">
                      <div className="flex justify-end">
                        <Button
                          size="xs"
                          color="light"
                          isProcessing={isButtonLoading}
                          disabled={isButtonLoading}
                          onClick={() => handleSend(queueDistribution._id)}
                        >
                          Gönder
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </>
      )}
    </CardContainer>
  );
};
